import React, { useEffect, useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Tooltip,
  Alert,
  CircularProgress,
  Collapse,
  Backdrop,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import RestoreIcon from "@mui/icons-material/Restore";
import AddIcon from "@mui/icons-material/Add";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import { fetchOrgChart as GetOrgChartService } from "../../Domain/Services/GetOrgChartService";
import { createNode, reassignNode } from "../../Domain/Services/OrgChartAdminServices";

/**
 * Detects if setting `childType.parentChartType = potentialParent` would create a cycle.
 * A cycle exists when potentialParent is already a descendant of childType.
 */
function wouldCreateCycle(items, childType, potentialParent) {
  if (!potentialParent || childType === potentialParent) return true;
  let current = potentialParent;
  const visited = new Set();
  while (current) {
    if (visited.has(current)) break; // already a cycle in the data, stop
    visited.add(current);
    const item = items.find((i) => i.chartType === current);
    if (!item || !item.parentChartType) break;
    if (item.parentChartType === childType) return true;
    current = item.parentChartType;
  }
  return false;
}

const OrgChartViewBuilder = ({ availableChartTypes, rootChartType = "GENERAL", rootTitle }) => {
  const [viewItems, setViewItems] = useState([]);
  const [savedOk, setSavedOk] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragIndexRef = useRef(null);
  const [structureNodesCache, setStructureNodesCache] = useState({});
  const fetchingRef = useRef(new Set());
  const normalizedRootChartType = (rootChartType || "GENERAL").toUpperCase();
  const resolvedRootTitle = rootTitle || normalizedRootChartType;

  const EXCLUDED_CHART_TYPES = ["VACACIONES", "EXTRAS"];

  useEffect(() => {
    let mounted = true;

    GetOrgChartService(normalizedRootChartType, { includeDetails: true })
      .then((response) => {
        if (!mounted) {
          return;
        }

        const nodes = Array.isArray(response) ? response : [];
        const nodeMap = new Map(nodes.map((node) => [node.id, node]));
        const itemsByChartType = new Map();

        nodes.forEach((node) => {
          const nodeChartType = (node.chartType || "").toUpperCase();
          if (
            nodeChartType === normalizedRootChartType ||
            EXCLUDED_CHART_TYPES.includes(nodeChartType)
          ) {
            return;
          }

          const parentNode = node.parent ? nodeMap.get(node.parent) : null;
          const parentChartType = (parentNode?.chartType || "").toUpperCase();
          const isStructureRoot = !parentNode || parentChartType !== nodeChartType;

          if (!isStructureRoot || itemsByChartType.has(nodeChartType)) {
            return;
          }

          itemsByChartType.set(nodeChartType, {
            chartType: node.chartType,
            parentChartType: parentNode && parentChartType !== normalizedRootChartType
              ? parentNode.chartType
              : null,
            parentNodeId: parentNode && parentChartType !== normalizedRootChartType
              ? parentNode.id
              : null,
          });
        });

        setViewItems(Array.from(itemsByChartType.values()));
      })
      .catch((err) => {
        if (!mounted) {
          return;
        }

        console.error(`Error loading ${normalizedRootChartType} public view:`, err);
        setError(`No se pudo cargar la estructura actual de ${resolvedRootTitle}.`);
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [normalizedRootChartType, resolvedRootTitle]);

  // Auto-fetch nodes for parent structures so the "Nodo destino" dropdown can render
  useEffect(() => {
    const parentTypes = new Set(
      viewItems
        .filter((item) => item.parentChartType)
        .map((item) => item.parentChartType)
    );
    parentTypes.forEach((chartType) => {
      if (!fetchingRef.current.has(chartType)) {
        fetchingRef.current.add(chartType);
        GetOrgChartService(chartType, { includeDetails: true })
          .then((nodes) => {
            setStructureNodesCache((prev) => ({
              ...prev,
              [chartType]: Array.isArray(nodes) ? nodes : [],
            }));
          })
          .catch((err) => {
            console.error(`Error loading nodes for ${chartType}:`, err);
            fetchingRef.current.delete(chartType);
          });
      }
    });
  }, [viewItems]);

  const usedTypes = viewItems.map((i) => i.chartType);
  const unusedTypes = availableChartTypes.filter(
    (t) =>
      t &&
      t.toUpperCase() !== normalizedRootChartType &&
      !usedTypes.includes(t)
  );

  const handleAdd = (chartType) => {
    setViewItems((prev) => [...prev, { chartType, parentChartType: null, parentNodeId: null }]);
    setSavedOk(false);
  };

  const handleRemove = (chartType) => {
    setViewItems((prev) =>
      prev
        .filter((i) => i.chartType !== chartType)
        .map((i) =>
          i.parentChartType === chartType
            ? { ...i, parentChartType: null, parentNodeId: null }
            : i
        )
    );
    setSavedOk(false);
  };

  const handleParentChange = (chartType, newParent) => {
    setViewItems((prev) =>
      prev.map((i) =>
        i.chartType === chartType
          ? { ...i, parentChartType: newParent || null, parentNodeId: null }
          : i
      )
    );
    setSavedOk(false);
  };

  const handleNodeChange = (chartType, nodeId) => {
    setViewItems((prev) =>
      prev.map((i) =>
        i.chartType === chartType
          ? { ...i, parentNodeId: nodeId || null }
          : i
      )
    );
    setSavedOk(false);
  };

  const handleDragStart = useCallback((e, index) => {
    dragIndexRef.current = index;
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  }, []);

  const handleDrop = useCallback((e, targetIndex) => {
    e.preventDefault();
    const sourceIndex = dragIndexRef.current;
    setDragOverIndex(null);
    if (sourceIndex === null || sourceIndex === targetIndex) return;
    setViewItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
    dragIndexRef.current = null;
    setSavedOk(false);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      const allChartTypes = availableChartTypes
        .filter((type) => type && type.toUpperCase() !== normalizedRootChartType);

      const [rootNodes, ...structureResults] = await Promise.all([
        GetOrgChartService(normalizedRootChartType, { includeDetails: true }),
        ...allChartTypes.map((chartType) => GetOrgChartService(chartType, { includeDetails: true })),
      ]);

      const rootData = Array.isArray(rootNodes) ? rootNodes : [];
      const rootNodeMap = new Map(rootData.map((node) => [node.id, node]));
      let rootNode = rootData.find((node) => {
        const parentNode = node.parent ? rootNodeMap.get(node.parent) : null;
        return (node.chartType || "").toUpperCase() === normalizedRootChartType && !parentNode;
      });

      if (!rootNode?.id) {
        const createdRoot = await createNode(resolvedRootTitle, normalizedRootChartType, null);
        if (createdRoot?.id) {
          rootNode = {
            id: createdRoot.id,
            chartType: normalizedRootChartType,
          };
        } else {
          throw new Error(`No se pudo crear el nodo ${resolvedRootTitle}.`);
        }
      }

      if (normalizedRootChartType !== "GENERAL") {
        await reassignNode(rootNode.id, null);
      }

      const rootIdsByChartType = new Map();
      structureResults.forEach((result, index) => {
        const chartType = allChartTypes[index];
        const nodes = Array.isArray(result) ? result : [];
        const nodeMap = new Map(nodes.map((node) => [node.id, node]));
        const rootNode = nodes.find((node) => {
          const parentNode = node.parent ? nodeMap.get(node.parent) : null;
          return !parentNode;
        });

        if (rootNode?.id) {
          rootIdsByChartType.set((chartType || "").toUpperCase(), rootNode.id);
        }
      });

      const configuredTypes = new Set(
        viewItems.map((item) => (item.chartType || "").toUpperCase())
      );

      const pendingByType = new Map(
        viewItems
          .map((item) => {
            const chartTypeKey = (item.chartType || "").toUpperCase();
            const rootId = rootIdsByChartType.get(chartTypeKey);
            if (!rootId) {
              return null;
            }

            const parentChartType = item.parentChartType
              ? (item.parentChartType || "").toUpperCase()
              : null;

            return [
              chartTypeKey,
              {
                rootId,
                parentChartType,
                parentNodeId: item.parentNodeId || null,
              },
            ];
          })
          .filter(Boolean)
      );

      // Apply updates in dependency order (parents first) to avoid transient invalid states.
      const appliedTypes = new Set();
      while (pendingByType.size > 0) {
        let progressed = false;

        for (const [chartTypeKey, pending] of pendingByType.entries()) {
          const parentReady = !pending.parentChartType
            || !pendingByType.has(pending.parentChartType)
            || appliedTypes.has(pending.parentChartType);

          if (!parentReady) {
            continue;
          }

          const parentId = pending.parentNodeId
            ? pending.parentNodeId
            : pending.parentChartType
              ? (rootIdsByChartType.get(pending.parentChartType) ?? rootNode.id)
              : rootNode.id;

          if (pending.rootId === parentId) {
            pendingByType.delete(chartTypeKey);
            appliedTypes.add(chartTypeKey);
            progressed = true;
            continue;
          }

          await reassignNode(pending.rootId, parentId);

          pendingByType.delete(chartTypeKey);
          appliedTypes.add(chartTypeKey);
          progressed = true;
        }

        if (!progressed) {
          throw new Error("No se pudo resolver el orden de dependencias entre estructuras.");
        }
      }

      // Remove old composition leftovers: if a structure is no longer configured,
      // detach its real root from the target hierarchy.
      for (const [chartTypeKey, rootId] of rootIdsByChartType.entries()) {
        if (configuredTypes.has(chartTypeKey)) {
          continue;
        }

        if (normalizedRootChartType === "GENERAL") {
          await reassignNode(rootId, null, { detachFromGeneral: true });
        } else {
          await reassignNode(rootId, null);
        }
      }

      setSavedOk(true);
    } catch (err) {
      console.error(`Error saving ${normalizedRootChartType} public view:`, err);
      const details = err?.message ? ` (${err.message})` : "";
      setError(`No se pudo guardar la estructura real de ${resolvedRootTitle}${details}.`);
      setSavedOk(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestore = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await GetOrgChartService(normalizedRootChartType, { includeDetails: true });
      const nodes = Array.isArray(response) ? response : [];
      const nodeMap = new Map(nodes.map((node) => [node.id, node]));
      const itemsByChartType = new Map();

      nodes.forEach((node) => {
        const nodeChartType = (node.chartType || "").toUpperCase();
        if (
          nodeChartType === normalizedRootChartType ||
          EXCLUDED_CHART_TYPES.includes(nodeChartType)
        ) {
          return;
        }

        const parentNode = node.parent ? nodeMap.get(node.parent) : null;
        const parentChartType = (parentNode?.chartType || "").toUpperCase();
        const isStructureRoot = !parentNode || parentChartType !== nodeChartType;

        if (!isStructureRoot || itemsByChartType.has(nodeChartType)) {
          return;
        }

        itemsByChartType.set(nodeChartType, {
          chartType: node.chartType,
          parentChartType: parentNode && parentChartType !== normalizedRootChartType
            ? parentNode.chartType
            : null,
          parentNodeId: parentNode && parentChartType !== normalizedRootChartType
            ? parentNode.id
            : null,
        });
      });

      setViewItems(Array.from(itemsByChartType.values()));
      setSavedOk(true);
    } catch (err) {
      console.error(`Error restoring ${normalizedRootChartType} public view:`, err);
      setError(`No se pudo recargar la estructura actual de ${resolvedRootTitle}.`);
      setSavedOk(false);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setViewItems([]);
    setSavedOk(false);
  };

  const buildTree = (items, parentChartType = null, depth = 0, visited = new Set()) => {
    const children = items.filter((i) => i.parentChartType === parentChartType);
    if (children.length === 0) return null;

    return children.map((item) => {
      if (visited.has(item.chartType)) return null; // cycle guard
      const nextVisited = new Set(visited).add(item.chartType);
      return (
        <Box key={item.chartType} sx={{ ml: depth * 2.5 }}>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ py: 0.3 }}>
            {depth > 0 && (
              <SubdirectoryArrowRightIcon sx={{ fontSize: 14, color: "grey.500" }} />
            )}
            <AccountTreeIcon sx={{ fontSize: 14, color: "primary.main" }} />
            <Typography variant="body2" sx={{ fontWeight: depth === 0 ? "bold" : "normal" }}>
              {item.chartType}
            </Typography>
          </Stack>
          {buildTree(items, item.chartType, depth + 1, nextVisited)}
        </Box>
      );
    });
  };

  return (
    <Box>
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 999,
          flexDirection: 'column',
          gap: 2,
          backgroundColor: 'rgba(0, 0, 0, 0.7)'
        }}
        open={loading || isSaving}
      >
        <CircularProgress color="inherit" size={60} thickness={4} />
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {isSaving ? `Guardando vista de ${resolvedRootTitle}...` : "Cargando configuración actual..."}
        </Typography>
        <Typography variant="body2">
          {isSaving 
            ? "Esto puede tardar unos segundos mientras se recalculan las jerarquías." 
            : "Por favor, espera un momento."}
        </Typography>
      </Backdrop>

      <Collapse in={!!(savedOk || error)}>
        <Box sx={{ mb: 2 }}>
          {savedOk && (
            <Alert 
              severity="success" 
              onClose={() => setSavedOk(false)}
              sx={{ fontWeight: 'bold' }}
            >
              Vista guardada correctamente.
            </Alert>
          )}
          {error && (
            <Alert 
              severity="error" 
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
        </Box>
      </Collapse>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", color: "primary.main" }}>
            {`Montar Vista ${resolvedRootTitle} Personalizada`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {`Elige qué estructuras cuelgan de ${resolvedRootTitle} y cómo se anidan entre sí. Esto reordena las raíces reales del organigrama.`}
          </Typography>
        </Box>
        <Tooltip title={`Recargar la estructura actual de ${resolvedRootTitle}`}>
          <span>
            <Button
              variant="outlined"
              startIcon={<RestoreIcon />}
              onClick={handleRestore}
              size="small"
              disabled={loading}
            >
              Restablecer
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          color={savedOk ? "success" : "primary"}
          size="small"
          disabled={loading || viewItems.length === 0}
          sx={{ minWidth: 120 }}
        >
          {savedOk ? "Guardado ✓" : "Guardar vista"}
        </Button>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">
        {/* Pool de estructuras disponibles */}
        <Box sx={{ minWidth: 180 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1, color: "text.secondary" }}>
            DISPONIBLES
          </Typography>
          <Paper
            variant="outlined"
            sx={{ p: 2, minHeight: 180, bgcolor: "#f9f9f9" }}
          >
            {loading ? (
              <Stack spacing={1} sx={{ opacity: 0.6 }}>
                {[1, 2, 3].map((i) => (
                  <Box key={i} sx={{ height: 32, bgcolor: '#eee', borderRadius: 1, animation: 'pulse 1.5s infinite ease-in-out' }} />
                ))}
                <style>{`
                  @keyframes pulse {
                    0% { opacity: 1; }
                    50% { opacity: 0.4; }
                    100% { opacity: 1; }
                  }
                `}</style>
              </Stack>
            ) : unusedTypes.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                {availableChartTypes.length === 0
                  ? "No hay estructuras creadas"
                  : "Todas en la vista"}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {unusedTypes.map((type) => (
                  <Chip
                    key={type}
                    label={type}
                    icon={<AddIcon />}
                    onClick={() => handleAdd(type)}
                    variant="outlined"
                    color="primary"
                    size="small"
                    sx={{ justifyContent: "flex-start", cursor: "pointer" }}
                  />
                ))}
              </Stack>
            )}
          </Paper>
        </Box>

        {/* Lista ordenable con selector de padre */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "text.secondary" }}>
              VISTA PERSONALIZADA ({viewItems.length} estructura{viewItems.length !== 1 ? "s" : ""})
            </Typography>
            {viewItems.length > 0 && (
              <Button
                size="small"
                color="error"
                onClick={handleClear}
                sx={{ textTransform: "none", fontSize: "0.72rem" }}
              >
                Limpiar todo
              </Button>
            )}
          </Stack>

          <Paper variant="outlined" sx={{ p: 2, minHeight: 180, bgcolor: "#f9f9f9" }}>
            {viewItems.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                <AccountTreeIcon sx={{ fontSize: 40, opacity: 0.25, mb: 1 }} />
                <Typography variant="body2">
                  Haz clic en una estructura de la izquierda para añadirla
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {viewItems.map((item, idx) => {
                  const availableParents = viewItems.filter(
                    (i) => i.chartType !== item.chartType && !wouldCreateCycle(viewItems, item.chartType, i.chartType)
                  );
                  const isDragTarget = dragOverIndex === idx;

                  // Compute selectable nodes for the "Nodo destino" dropdown
                  const parentStructureNodes = item.parentChartType
                    ? (structureNodesCache[item.parentChartType] || [])
                    : [];
                  const parentNodeMap = new Map(parentStructureNodes.map((n) => [n.id, n]));
                  const selectableNodes = parentStructureNodes.filter((n) => {
                    if (!n.parent) return false;
                    const p = parentNodeMap.get(n.parent);
                    return p && (p.chartType || "").toUpperCase() === (n.chartType || "").toUpperCase();
                  });

                  return (
                    <Paper
                      key={item.chartType}
                      variant="outlined"
                      draggable
                      onDragStart={(e) => handleDragStart(e, idx)}
                      onDragOver={(e) => handleDragOver(e, idx)}
                      onDrop={(e) => handleDrop(e, idx)}
                      onDragEnd={handleDragEnd}
                      sx={{
                        p: 1.5,
                        bgcolor: "white",
                        cursor: "grab",
                        "&:active": { cursor: "grabbing" },
                        borderColor: isDragTarget ? "primary.main" : "grey.300",
                        borderWidth: isDragTarget ? 2 : 1,
                        transition: "border-color 0.1s",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <DragIndicatorIcon sx={{ color: "grey.400", flexShrink: 0 }} />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: "bold", flex: 1, userSelect: "none" }}
                        >
                          {item.chartType}
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: 170 }}>
                          <InputLabel>Cuelga bajo…</InputLabel>
                          <Select
                            value={item.parentChartType || ""}
                            label="Cuelga bajo…"
                            onChange={(e) =>
                              handleParentChange(item.chartType, e.target.value)
                            }
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MenuItem value="">
                              <em>Raíz (sin padre)</em>
                            </MenuItem>
                            {availableParents.map((p) => (
                              <MenuItem key={p.chartType} value={p.chartType}>
                                {p.chartType}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {item.parentChartType && selectableNodes.length > 0 && (
                          <FormControl size="small" sx={{ minWidth: 170 }}>
                            <InputLabel>Nodo destino</InputLabel>
                            <Select
                              value={item.parentNodeId ?? ""}
                              label="Nodo destino"
                              onChange={(e) =>
                                handleNodeChange(
                                  item.chartType,
                                  e.target.value ? Number(e.target.value) : null
                                )
                              }
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MenuItem value="">
                                <em>Raíz</em>
                              </MenuItem>
                              {selectableNodes.map((n) => (
                                <MenuItem key={n.id} value={n.id}>
                                  {n.text}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                        <Tooltip title="Quitar de la vista">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemove(item.chartType)}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </Box>

        {/* Vista previa del árbol */}
        {viewItems.length > 0 && (
          <Box sx={{ minWidth: 180 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1, color: "text.secondary" }}>
              VISTA PREVIA
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, minHeight: 180 }}>
              {buildTree(viewItems)}
            </Paper>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

OrgChartViewBuilder.propTypes = {
  availableChartTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  rootChartType: PropTypes.string,
  rootTitle: PropTypes.string,
};

export default OrgChartViewBuilder;
